import React, { useState, useEffect } from 'react';
import { X, Zap, ChevronDown, Link } from 'lucide-react';

const IntegrationTutorial = ({
  targetSelector = '.integration-button',
  onIntegrationClick,
  storageKey = 'oqlous_tutorial_seen',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [targetElement, setTargetElement] = useState(null);
  const [spotlightPosition, setSpotlightPosition] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const getTooltipPosition = () => {
    const tooltipWidth = 256;
    const screenWidth = window.innerWidth;
    const left = spotlightPosition.x + spotlightPosition.width / 2;
    const minLeft = tooltipWidth / 2 + 16;
    const maxLeft = screenWidth - tooltipWidth / 2 - 16;
    return Math.min(Math.max(left, minLeft), maxLeft);
  };

  const getIndicatorPosition = () => {
    const indicatorX = spotlightPosition.x + spotlightPosition.width + 10;
    const screenWidth = window.innerWidth;
    return Math.min(indicatorX, screenWidth - 32);
  };

  useEffect(() => {
    const tutorialSeen = localStorage.getItem(storageKey);
    if (!tutorialSeen) {
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, [storageKey]);

  useEffect(() => {
    if (isVisible && targetSelector) {
      const element = document.querySelector(targetSelector);
      if (element) {
        setTargetElement(element);
        updateSpotlightPosition(element);
      }
    }
  }, [isVisible, targetSelector]);

  useEffect(() => {
    const handleResize = () => {
      if (targetElement) {
        updateSpotlightPosition(targetElement);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [targetElement]);

  const updateSpotlightPosition = (element) => {
    const rect = element.getBoundingClientRect();
    const padding = 8;
    setSpotlightPosition({
      x: rect.left - padding,
      y: rect.top - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });
  };

  const handleClose = () => {
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
  };

  const handleIntegrationAction = () => {
    localStorage.setItem(storageKey, 'true');
    if (onIntegrationClick) onIntegrationClick();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-50">
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          clipPath: targetElement
            ? `polygon(0% 0%, 0% 100%, ${spotlightPosition.x}px 100%, ${spotlightPosition.x}px ${spotlightPosition.y}px, ${spotlightPosition.x + spotlightPosition.width}px ${spotlightPosition.y}px, ${spotlightPosition.x + spotlightPosition.width}px ${spotlightPosition.y + spotlightPosition.height}px, ${spotlightPosition.x}px ${spotlightPosition.y + spotlightPosition.height}px, ${spotlightPosition.x}px 100%, 100% 100%, 100% 0%)`
            : 'none',
        }}
      />

      {targetElement && (
        <div
          className="absolute animate-pulse rounded-lg border-2 border-white transition-all duration-500"
          style={{
            left: `${spotlightPosition.x}px`,
            top: `${spotlightPosition.y}px`,
            width: `${spotlightPosition.width}px`,
            height: `${spotlightPosition.height}px`,
            boxShadow:
              '0 0 0 4px rgba(255, 255, 255, 0.3), 0 0 20px rgba(255, 255, 255, 0.5)',
          }}
        />
      )}

      {targetElement && (
        <div
          className="absolute"
          style={{
            left: `${getIndicatorPosition()}px`,
            top: `${spotlightPosition.y - 10}px`,
          }}
        >
          <div className="relative">
            <div className="h-6 w-6 animate-ping rounded-full bg-gray-700 opacity-75" />
            <div className="absolute inset-0 flex h-6 w-6 animate-pulse items-center justify-center rounded-full bg-gray-700">
              <Zap size={12} className="text-gray-200" />
            </div>
          </div>
        </div>
      )}

      <div
        className="animate-in fade-in slide-in-from-bottom-4 pointer-events-auto absolute duration-500"
        style={{
          top: `${spotlightPosition.y + spotlightPosition.height + 20}px`,
          left: `${getTooltipPosition()}px`,
          transform: 'translateX(-50%)',
          maxWidth: '90vw',
        }}
      >
        <div className="w-64 max-w-xs overflow-hidden rounded-xl border border-gray-600 bg-gray-700 shadow-2xl">
          <div className="relative bg-gray-900 p-4 text-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 animate-pulse rounded-full bg-gray-400" />
                <span className="text-sm font-medium">Welcome to Oqlous</span>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-200 transition-colors hover:text-gray-300"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="p-4">
            <h3 className="mb-2 text-base font-bold text-gray-200">
              Integrate Apps First
            </h3>

            <p className="mb-4 text-sm text-gray-300">
              Connect your apps before prompting for better AI assistance.
            </p>

            <div className="space-y-2">
              <button
                onClick={handleIntegrationAction}
                className="flex w-full items-center justify-center space-x-2 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-gray-200 transition-colors hover:bg-gray-800"
              >
                <Link size={14} />
                <span>Integrate Apps</span>
              </button>

              <button
                onClick={handleClose}
                className="w-full py-1 text-xs text-gray-400 transition-colors hover:text-gray-200"
              >
                Skip for now
              </button>
            </div>
          </div>

          <div className="absolute -top-2 left-1/2 -translate-x-1/2 transform">
            <ChevronDown
              className="text-gray-200"
              size={20}
              style={{ filter: 'drop-shadow(0 -2px 4px rgba(0,0,0,0.1))' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationTutorial;
